import React, { useEffect, useState } from "react";
import axios from "axios";
import exportValue from "../../apiconfig";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import html2pdf from "html2pdf.js";

const InvoiceList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState({
    month: "",
    year: "",
    search: "",
  });

  const [invoiceList, setInvoiceList] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
    total: 0,
  });
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  console.log("selectedInvoices ==", selectedInvoices);
  const [isLoadingPrint, setIsLoadingPrint] = useState(false);

  useEffect(() => {
    getInvoices();
  }, [pagination.page, pagination.limit]);

  const handleChange = (e) => {
    setFilter({
      ...filter,
      [e.target.name]: e.target.value,
    });
  };

  const getInvoices = async () => {
    try {
      setLoading(true);

      const response = await axios.post(
        exportValue.host +
          "/" +
          exportValue.version +
          "/" +
          exportValue.api +
          "/invoice_list",
        {
          ...filter,
          page: pagination.page,
          limit: pagination.limit,
        },
        { headers: exportValue.headers }
      );

      if (response.data.success) {
        setInvoiceList(response.data.data);

        setPagination({
          ...pagination,
          total: response.data.total,
        });
      }
    } catch (err) {
      console.log(err);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Unable to fetch invoices.",
      });
    }

    setLoading(false);
  };

  const downloadReport = async (item) => {
    try {
      const response = await axios.post(
        exportValue.host +
          "/" +
          exportValue.version +
          "/" +
          exportValue.api +
          "/download_invoice_report",
        {
          customer_id: item.customer_id,
          invoice_id: item.invoice_id,
        },
        {
          headers: exportValue.headers,
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));

      const link = document.createElement("a");

      link.href = url;

      link.setAttribute("download", `${item.invoice_id}.xlsx`);

      document.body.appendChild(link);

      link.click();

      link.remove();

      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.log(e);

      Swal.fire("Error", "Unable to download report", "error");
    }
  };

  const handlePageChange = (newPage) => {
    setPagination({
      ...pagination,
      page: newPage,
    });
  };

  const handleLimitChange = (e) => {
    setPagination({
      ...pagination,
      limit: Number(e.target.value),
      page: 1,
    });
  };

  const downloadExcelInvoice = async () => {
    const response = await axios.post(
      exportValue.host +
        "/" +
        exportValue.version +
        "/" +
        exportValue.api +
        "/download_invoice_list",
      {
        ...filter,
      },
      { headers: exportValue.headers }
    );

    if (response.data.success) {
      const invoices = response.data.data;
      if (!invoices || invoices.length === 0) return;
      const excelData = invoices.map((sub) => ({
        "Invoice No": sub.invoice_id,
        Customer:
          sub.customer_snapshot?.company_name ||
          sub.customer_snapshot?.full_name ||
          "",
        "JP ID": sub.customer_snapshot?.jp_id || "",
        State: sub.customer_snapshot?.state_name || "",
        "GST / PAN":
          sub.customer_snapshot.gst_number &&
          sub.customer_snapshot.gst_number !== "undefined"
            ? ` ${sub.customer_snapshot.gst_number}`
            : sub.customer_snapshot.pan_number
            ? ` ${sub.customer_snapshot.pan_number}`
            : "",
        "Invoice Date": sub.invoice_date,
        "Invoice Period": sub.invoice_period,
        "Logistic Services": Number(sub.taxable_amount).toFixed(2),
        GST: Number(sub.cgst + sub.sgst + sub.igst).toFixed(2),
        Total: sub.total_amount || 0,
      }));

      // Convert JSON → Sheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Create Workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Invoices");

      // Download file
      XLSX.writeFile(workbook, "Invoices.xlsx");
    }
  };

  const handleSelectInvoice = (invoiceId) => {
    setSelectedInvoices((prev) => {
      if (prev.includes(invoiceId)) {
        return prev.filter((id) => id !== invoiceId);
      }

      return [...prev, invoiceId];
    });
  };

  const handleSelectAll = () => {
    if (selectedInvoices.length === invoiceList.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(invoiceList.map((item) => item.invoice_id));
    }
  };

  const getInvoiceForPDF = async (invoiceId) => {
    const response = await axios.post(
      exportValue.host +
        "/" +
        exportValue.version +
        "/" +
        exportValue.api +
        "/get_single_invoice",
      {
        invoice_id: invoiceId,
      },
      {
        headers: exportValue.headers,
      }
    );

    if (!response.data.success) {
      throw new Error(response.data.message || "Unable to fetch invoice");
    }

    return response.data.data;
  };

  const handleBulkPrint = async () => {
    if (selectedInvoices.length === 0) {
      Swal.fire(
        "Select Invoice",
        "Please select at least one invoice.",
        "warning"
      );

      return;
    }

    setIsLoadingPrint(true);

    try {
      for (const invoiceId of selectedInvoices) {
        const invoiceData = await getInvoiceForPDF(invoiceId);

        await generateInvoicePDF(invoiceData);
      }

      Swal.fire(
        "Success",
        `${selectedInvoices.length} invoice(s) downloaded successfully.`,
        "success"
      );
    } catch (error) {
      console.error("Bulk invoice PDF error:", error);

      Swal.fire("Error", "Unable to generate one or more invoices.", "error");
    } finally {
      setIsLoadingPrint(false);
    }
  };

  const generateInvoicePDF = (invoiceData) => {
    return new Promise((resolve, reject) => {
      try {
        const invoiceHTML = generateCombinedHTML(invoiceData);

        const tempContainer = document.createElement("div");

        tempContainer.innerHTML = invoiceHTML;

        // tempContainer.style.position = "absolute";
        // tempContainer.style.left = "-9999px";
        // tempContainer.style.top = "0";
        // tempContainer.style.width = "210mm";

        document.body.appendChild(tempContainer);

        html2pdf()
          .from(tempContainer)
          .set({
            margin: 10,

            filename: `${invoiceData.invoice.invoice_id}.pdf`,

            image: {
              type: "jpeg",
              quality: 0.98,
            },

            html2canvas: {
              scale: 2,
              useCORS: true,
            },

            jsPDF: {
              unit: "mm",
              format: "a4",
              orientation: "portrait",
            },
          })
          .save()
          .then(() => {
            // document.body.removeChild(tempContainer);
            resolve();
          })
          .catch((error) => {
            // document.body.removeChild(tempContainer);
            reject(error);
          });
      } catch (error) {
        reject(error);
      }
    });
  };
  const calculateDueDate = (initialDate, daysToAdd) => {
    const initialDateObject = new Date(initialDate);
    const dueDateObject = new Date(initialDateObject);
    dueDateObject.setDate(initialDateObject.getDate() + daysToAdd);

    // Format the due date to YYYY/MM/DD
    const formattedDueDate = `${dueDateObject.getFullYear()}-${
      dueDateObject.getMonth() + 1
    }-${dueDateObject.getDate()}`;

    return formattedDueDate;
  };

  const generateCombinedHTML = (invoiceData) => {
    const { invoice, customer, company } = invoiceData;

    const isMaharashtra =
      String(customer?.state_name || "").toLowerCase() === "maharashtra";

    const taxableAmount = Number(invoice?.taxable_amount || 0);
    const cgst = Number(invoice?.cgst || 0);
    const sgst = Number(invoice?.sgst || 0);
    const igst = Number(invoice?.igst || 0);
    const adjustment = Number(invoice?.adjustment_amount || 0);
    const totalAmount = Number(invoice?.total_amount || 0);

    const gstNumber =
      customer?.gst_number && customer.gst_number !== "undefined"
        ? customer.gst_number
        : "";

    const panNumber =
      customer?.pan_number && customer.pan_number !== "undefined"
        ? customer.pan_number
        : "";

    const gstinPan = gstNumber || panNumber || "";

    const signatureHTML = `
        <div style="text-align:right;">
          <img
            src="../../assets/img/signature.png"
            alt="Signature"
            style="max-width:120px; max-height:70px;"
          />
        </div>
      `;
    return `
      <div
        style="
          width: 100%;
          font-family: Arial, sans-serif;
          font-size: 11px;
          color: #000;
          background: #fff;
          padding: 10px;
          box-sizing: border-box;
        "
      >
  
        <!-- HEADER -->
  
        <div
          style="
            border: 1px solid #ddd;
            padding: 15px;
            margin-bottom: 5px;
          "
        >
  
          <div style="margin-bottom:20px;">
            <img
              src="${window.location.origin}/assets/img/logo_5.png"
              style="height:55px;"
            />
          </div>
  
          <table style="width:100%; border-collapse:collapse;">
            <tr>
  
              <td
                style="
                  width:60%;
                  vertical-align:top;
                  line-height:18px;
                "
              >
  
                <strong>
                  ${company?.invoice_name || ""}
                </strong>
                <br/>
  
                ${company?.invoice_address || ""}
                <br/>
  
                Phone:
                ${company?.invoice_phone || ""}
                <br/>
  
                Email:
                ${company?.invoice_email || ""}
                <br/><br/>
  
                <strong>PAN Number:</strong>
                ${company?.invoice_pan || ""}
                <br/>
  
                <strong>CIN Number:</strong>
                ${company?.invoice_cin || ""}
                <br/>
  
                <strong>GSTIN:</strong>
                ${company?.invoice_gst || ""}
                <br/>
  
                <strong>IRN:</strong>
                ${company?.invoice_irn || ""}
  
              </td>
  
              <td
                style="
                  width:40%;
                  vertical-align:top;
                  line-height:20px;
                "
              >
  
                <h3 style="margin-top:0;">
                  TAX INVOICE
                </h3>
  
                <h2 style="color:green;">
                  PAID
                </h2>
  
                <strong>Invoice No. :</strong>
                ${invoice?.invoice_id || ""}
                <br/>
  
                <strong>Invoice Date :</strong>
                ${invoice?.invoice_date || ""}
                <br/>
  
                <strong>Due Date :</strong>
                ${calculateDueDate(invoice?.invoice_date, 15)}
                <br/>
  
                <strong>Invoice Period :</strong>
                ${getInvoicePeriod(invoice?.invoice_period)}
  
              </td>
  
            </tr>
          </table>
  
          <hr/>
  
          <!-- INVOICE TO -->
  
          <table style="width:100%; border-collapse:collapse;">
            <tr>
  
              <td
                style="
                  width:60%;
                  vertical-align:top;
                  line-height:20px;
                "
              >
  
                <strong>Invoice To:</strong>
                <br/>
  
                ${customer?.company_name || customer?.full_name || ""}
  
                <br/>
  
                ${customer?.address || ""}
                <br/>
  
                ${customer?.city_name || ""}
                ${customer?.city_name && customer?.state_name ? "," : ""}
                ${customer?.state_name || ""}
  
              </td>
  
              <td
                style="
                  width:40%;
                  vertical-align:top;
                  line-height:20px;
                "
              >
  
                <strong>GSTIN/PAN:</strong>
                ${gstinPan}
                <br/>
  
                <strong>State Code:</strong>
                ${customer?.state_code || ""}
                <br/>
  
                <strong>Place of Supply:</strong>
                ${customer?.place_of_supply || customer?.state_name || ""}
                <br/>
  
                <strong>Reverse Charge:</strong>
                ${customer?.reverse_charge || "No"}
  
              </td>
  
            </tr>
          </table>
  
        </div>
  
  
        <!-- SERVICES -->
  
        <div
          style="
            border:1px solid #ddd;
            padding:10px;
            margin-bottom:5px;
          "
        >
  
          <table
            style="
              width:100%;
              border-collapse:collapse;
            "
          >
  
            <thead>
  
              <tr>
                <th
                  style="
                    border:1px solid #ddd;
                    padding:7px;
                    text-align:left;
                  "
                >
                  SAC
                </th>
  
                <th
                  style="
                    border:1px solid #ddd;
                    padding:7px;
                    text-align:left;
                  "
                >
                  Description
                </th>
  
                <th
                  style="
                    border:1px solid #ddd;
                    padding:7px;
                    text-align:right;
                  "
                >
                  Amount
                </th>
              </tr>
  
            </thead>
  
            <tbody>
  
              <tr>
  
                <td
                  style="
                    border:1px solid #ddd;
                    padding:7px;
                  "
                >
                  996812
                </td>
  
                <td
                  style="
                    border:1px solid #ddd;
                    padding:7px;
                  "
                >
                  Logistics Services
                </td>
  
                <td
                  style="
                    border:1px solid #ddd;
                    padding:7px;
                    text-align:right;
                  "
                >
                  ₹${taxableAmount.toFixed(2)}
                </td>
  
              </tr>
  
  
              ${
                isMaharashtra
                  ? `
  
                    <tr>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                        "
                      ></td>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                        "
                      >
                        CGST @ 9%
                      </td>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                          text-align:right;
                        "
                      >
                        ₹${cgst.toFixed(2)}
                      </td>
  
                    </tr>
  
                    <tr>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                        "
                      ></td>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                        "
                      >
                        SGST @ 9%
                      </td>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                          text-align:right;
                        "
                      >
                        ₹${sgst.toFixed(2)}
                      </td>
  
                    </tr>
                  `
                  : `
  
                    <tr>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                        "
                      ></td>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                        "
                      >
                        IGST @ 18%
                      </td>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                          text-align:right;
                        "
                      >
                        ₹${igst.toFixed(2)}
                      </td>
  
                    </tr>
                  `
              }
  
  
              ${
                adjustment !== 0
                  ? `
                    <tr>
  
                      <td
                        colspan="2"
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                        "
                      >
                        Adjustment
                      </td>
  
                      <td
                        style="
                          border:1px solid #ddd;
                          padding:7px;
                          text-align:right;
                        "
                      >
                        ₹${adjustment.toFixed(2)}
                      </td>
  
                    </tr>
                  `
                  : ""
              }
  
  
              <tr>
  
                <td
                  colspan="2"
                  style="
                    border:1px solid #ddd;
                    padding:7px;
                  "
                >
                  <strong>Grand Total</strong>
                </td>
  
                <td
                  style="
                    border:1px solid #ddd;
                    padding:7px;
                    text-align:right;
                  "
                >
                  <strong>
                    ₹${totalAmount.toFixed(2)}
                  </strong>
                </td>
  
              </tr>
  
            </tbody>
  
          </table>
  
        </div>
  
  
        <!-- BANK DETAILS -->
  
        <div
          style="
            border:1px solid #ddd;
            padding:10px;
            margin-bottom:5px;
          "
        >
  
          <strong>
            Bank and Other Commercial Details
          </strong>
  
          <hr/>
  
          All Payments by transfer/check/DD should be drawn in favour of
          <br/>
  
          <strong>Entity Name:</strong>
          ${company?.entity_name || ""}
          <br/>
  
          <strong>Account number:</strong>
          ${company?.account_number || ""}
          <br/>
  
          <strong>Bank:</strong>
          ${company?.bank || ""}
          <br/>
  
          <strong>Branch:</strong>
          ${company?.branch || ""}
          <br/>
  
          <strong>RTGS/NEFT/IFSC Code:</strong>
          ${company?.ifsc_code || ""}
  
          <div style="margin-top:15px;">
            ${signatureHTML}
          </div>
  
        </div>
  
      </div>
    `;
  };
  const getInvoicePeriod = (monthYear) => {
    if (!monthYear) return "";

    const [monthName, year] = monthYear.split(",");

    const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth();

    const startDate = new Date(year, monthIndex, 1);

    const endDate = new Date(year, monthIndex + 1, 0);

    const formatDate = (date) => {
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(date.getDate()).padStart(2, "0")}`;
    };

    return `${formatDate(startDate)} to ${formatDate(endDate)}`;
  };

  return (
    <div className="container-fluid">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white">
          <div className="row">
            <div className="col-md-3">
              <input
                className="form-control"
                placeholder="Search"
                name="search"
                value={filter.search}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-2">
              <select
                className="form-select"
                name="month"
                value={filter.month}
                onChange={handleChange}
              >
                <option value="">Month</option>
                <option value="01">January</option>
                <option value="02">February</option>
                <option value="03">March</option>
                <option value="04">April</option>
                <option value="05">May</option>
                <option value="06">June</option>
                <option value="07">July</option>
                <option value="08">August</option>
                <option value="09">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>

            <div className="col-md-2">
              <input
                type="number"
                className="form-control"
                placeholder="Year"
                name="year"
                value={filter.year}
                onChange={handleChange}
              />
            </div>

            <div className="col-md-2">
              <button
                className="btn btn-primary w-100"
                onClick={() => {
                  setPagination({
                    ...pagination,
                    page: 1,
                  });
                  getInvoices();
                }}
              >
                Search
              </button>
            </div>
            <div class="col-2 ">
              <button
                className="btn btn-primary g-1"
                onClick={downloadExcelInvoice}
              >
                Download Excel
              </button>
            </div>
          </div>
        </div>

        <div className="card-body table-responsive">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <label className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={
                    invoiceList.length > 0 &&
                    selectedInvoices.length === invoiceList.length
                  }
                  onChange={handleSelectAll}
                />

                <span className="form-check-label ms-2">Select All</span>
              </label>
            </div>

            <button
              className="btn btn-primary"
              disabled={selectedInvoices.length === 0 || isLoadingPrint}
              onClick={handleBulkPrint}
            >
              {isLoadingPrint ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Generating...
                </>
              ) : (
                <>
                  <i className="bi bi-download me-1"></i>
                  Download Selected
                </>
              )}
            </button>
          </div>
          <table className="table table-bordered table-hover">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      invoiceList.length > 0 &&
                      selectedInvoices.length === invoiceList.length
                    }
                    onChange={handleSelectAll}
                  />
                </th>

                <th>Invoice No.</th>

                <th>JP ID</th>

                <th>Customer</th>

                <th>Invoice Period</th>

                <th>Logistics Services</th>

                <th>GST</th>

                <th>Total</th>

                <th>Status</th>

                <th width="180">Action</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : invoiceList.length === 0 ? (
                <tr>
                  <td colSpan="9" className="text-center">
                    No Invoice Found
                  </td>
                </tr>
              ) : (
                invoiceList.map((item, index) => (
                  <tr key={item.invoice_id}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedInvoices.includes(item.invoice_id)}
                        onChange={() => handleSelectInvoice(item.invoice_id)}
                      />
                    </td>

                    <td>{item.invoice_id}</td>
                    <td>{item.customer_snapshot?.jp_id || ""}</td>

                    <td>
                      <b>{item.customer_snapshot?.company_name}</b>

                      <br />

                      <small>{item.customer_id}</small>
                    </td>

                    <td>{item.invoice_period}</td>

                    <td>₹{Number(item.taxable_amount).toFixed(2)}</td>

                    <td>
                      ₹{Number(item.cgst + item.sgst + item.igst).toFixed(2)}
                    </td>

                    <td>
                      <b>₹{Number(item.total_amount).toFixed(2)}</b>
                    </td>

                    <td>
                      {item.status === 1 ? (
                        <span className="badge bg-success">Generated</span>
                      ) : (
                        <span className="badge bg-danger">Cancelled</span>
                      )}
                    </td>

                    <td>
                      <button
                        className="btn btn-sm btn-primary me-2"
                        onClick={() =>
                          navigate(
                            "/invoice_view/" +
                              encodeURIComponent(item.invoice_id)
                          )
                        }
                      >
                        View
                      </button>

                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => downloadReport(item)}
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Showing{" "}
              {invoiceList.length > 0
                ? (pagination.page - 1) * pagination.limit + 1
                : 0}
              {" - "}
              {(pagination.page - 1) * pagination.limit + invoiceList.length}
              {" of "}
              {pagination.total}
            </div>

            <div className="d-flex gap-2">
              <select
                className="form-select"
                value={pagination.limit}
                onChange={handleLimitChange}
                style={{ width: "100px" }}
              >
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="300">300</option>
                <option value="400">400</option>
                <option value="500">500</option>
                <option value="1000">1000</option>
              </select>

              <button
                className="btn btn-outline-primary"
                disabled={pagination.page === 1}
                onClick={() => handlePageChange(pagination.page - 1)}
              >
                Previous
              </button>

              <button className="btn btn-primary">{pagination.page}</button>

              <button
                className="btn btn-outline-primary"
                disabled={
                  pagination.page * pagination.limit >= pagination.total
                }
                onClick={() => handlePageChange(pagination.page + 1)}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceList;
